import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Users, Clock, LogOut, Loader2 } from 'lucide-react';
import axios from 'axios';
import { TeamRosterDialog } from './TeamRosterDialog';
import { ProjectJobSelect, errorDetailText } from './ProjectJobSelect';

const API = `${window.__BACKEND_URL__}/api`;

const selectStyle = {
  background: '#0F1923',
  border: '1px solid #2D3748',
  color: '#E2E8F0',
};

export const TeamQuickClock = ({ onPunch }) => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [squads, setSquads] = useState([]);
  const [targetId, setTargetId] = useState('');
  // Project REQUIRED (Sep 2026); job optional under it.
  const [attr, setAttr] = useState({ projectId: null, jobId: null });
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  // Roster dialog (Aug 2026): pick who's on shift + add temps before punching
  const [rosterMode, setRosterMode] = useState(null); // null | 'in' | 'out'

  const h = { Authorization: `Bearer ${token}` };

  const refreshMembers = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/teams/${id}/members-details`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(res.data.members || []);
    } catch (e) { /* silent */ }
  }, [token]);

  const loadSquads = useCallback(async (id) => {
    try {
      const subsRes = await axios.get(`${API}/teams/${id}/subteams`, { headers: { Authorization: `Bearer ${token}` } });
      setSquads(subsRes.data.subteams || []);
    } catch (e) { setSquads([]); }
  }, [token]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const teamsRes = await axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } });
        const topLevel = teamsRes.data.filter(t => !t.parent_team_id && !t.is_subteam);
        const supervised = topLevel.filter(
          t => (t.supervisor_ids || []).includes(user.id) || t.supervisor_id === user.id
        );
        // Admins can quick-clock ANY team (Jun 2026 team picker); supervisors
        // see the teams they supervise. Supervised teams sort first so the
        // default selection is still "my" team.
        const mine = user.role === 'admin'
          ? [...supervised, ...topLevel.filter(t => !supervised.some(s => s.id === t.id))]
          : supervised;
        if (mine.length > 0) {
          setTeams(mine);
          setTeamId(mine[0].id);
          setTargetId(mine[0].id);
          refreshMembers(mine[0].id);
          loadSquads(mine[0].id);
        }
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, [user]);

  const team = teams.find(t => t.id === teamId);
  if (!team) return null;

  const clockedIn = members.filter(m => m.is_clocked_in).length;
  const total = members.length;
  const targetName = targetId === team.id ? team.name : (squads.find(s => s.id === targetId)?.name || team.name);

  const selectTeam = (id) => {
    setTeamId(id);
    setTargetId(id);
    setMembers([]);
    refreshMembers(id);
    loadSquads(id);
  };

  const selectTarget = (id) => {
    setTargetId(id);
    setMembers([]);
    refreshMembers(id);
  };

  const confirmRoster = async ({ memberIds, extraIds, absences }) => {
    setBusy(true);
    try {
      if (rosterMode === 'in') {
        const res = await axios.post(`${API}/teams/clock-in`, {
          team_id: targetId,
          project_id: attr.projectId,
          job_id: attr.jobId || null,
          send_notifications: true,
          member_ids: memberIds,
          extra_member_ids: extraIds,
          absences: absences || [],
        }, { headers: h });
        const tempNote = extraIds.length > 0 ? ` (incl. ${extraIds.length} temp)` : '';
        toast({ title: `${targetName} Clocked In`, description: `${res.data.clocked_in} member(s) clocked in${tempNote}` });
      } else {
        const res = await axios.post(`${API}/teams/clock-out`, { team_id: targetId, member_ids: memberIds }, { headers: h });
        toast({ title: `${targetName} Clocked Out`, description: `${res.data.clocked_out} member(s) clocked out` });
      }
      setRosterMode(null);
      refreshMembers(targetId);
      if (onPunch) onPunch();
    } catch (e) {
      toast({ title: rosterMode === 'in' ? 'Clock In Failed' : 'Clock Out Failed', description: errorDetailText(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="ht-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="team-quick-clock">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(30,136,229,0.15)' }}>
          <Users className="w-5 h-5" style={{ color: '#1E88E5' }} />
        </div>
        <div className="min-w-0">
          {teams.length > 1 ? (
            <select
              value={teamId}
              onChange={(e) => selectTeam(e.target.value)}
              className="max-w-[220px] rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none cursor-pointer"
              style={{ ...selectStyle, color: '#FFFFFF' }}
              title="Switch team"
              data-testid="quick-clock-team-select"
            >
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          ) : (
            <p className="text-sm font-semibold text-white truncate">{targetName}</p>
          )}
          <p className="text-xs mt-0.5" style={{ color: '#64748B' }} data-testid="quick-clock-count">
            <span style={{ color: clockedIn > 0 ? '#00C896' : '#64748B' }}>{clockedIn}</span>/{total} on the clock
            {teams.length > 1 && targetId !== team.id && <span> · {targetName}</span>}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {squads.length > 0 && (
          <select
            value={targetId}
            onChange={(e) => selectTarget(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs font-medium focus:outline-none"
            style={selectStyle}
            data-testid="quick-clock-target-select"
          >
            <option value={team.id}>Whole Team — {team.name}</option>
            {squads.map(s => (
              <option key={s.id} value={s.id}>Squad — {s.name} ({(s.member_ids || []).length})</option>
            ))}
          </select>
        )}
        <ProjectJobSelect token={token} projectId={attr.projectId} jobId={attr.jobId} onChange={setAttr} compact testIdPrefix="quick-clock" />
        <button
          onClick={() => setRosterMode('in')}
          disabled={busy || !attr.projectId}
          title={!attr.projectId ? 'Pick a project first' : ''}
          className="px-4 py-2 rounded-xl text-xs font-semibold text-white disabled:opacity-40 ht-btn-glow"
          style={{ background: '#00C896' }}
          data-testid="quick-team-clock-in-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Clock className="w-3.5 h-3.5 inline mr-1" />} Clock In {targetId === team.id ? 'Team' : 'Squad'}
        </button>
        <button
          onClick={() => setRosterMode('out')}
          disabled={busy || clockedIn === 0}
          className="px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-40"
          style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.4)' }}
          data-testid="quick-team-clock-out-btn"
        >
          <LogOut className="w-3.5 h-3.5 inline mr-1" /> Clock Out {targetId === team.id ? 'Team' : 'Squad'}
        </button>
      </div>

      <TeamRosterDialog
        open={!!rosterMode}
        onClose={() => setRosterMode(null)}
        mode={rosterMode || 'in'}
        targetId={targetId}
        targetName={targetName}
        token={token}
        busy={busy}
        onConfirm={confirmRoster}
      />
    </div>
  );
};
